import React, { useEffect, useRef, useState } from 'react';
import HoverCard from '../../../components/HoverCard.jsx';
import { useChatDock } from '../../../contexts/ChatDockContext';
import { FaComments, FaEnvelopeOpen } from 'react-icons/fa';
import './ContactsSidebar.css';

const getInitials = (nameOrEmail = '') => {
  const s = String(nameOrEmail).trim();
  if (!s) return '?';
  const parts = s.split(' ');
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s[0]?.toUpperCase() || '?';
};

const ContactsSidebar = ({
  contacts,
  requestContacts = [],
  activeContact,
  setActiveContact,
  onEditGroup,
  onRemoveGroup,
  onCreateGroup,
  onOpenSettings,
  onSearchChange,
  lastMessages = {},
  lastSenderNames = {},
  unreadCounts = {},
  userId,
  onMarkUnread,
  enrichedContacts = {}
}) => {
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [query, setQuery] = useState('');
  const debounceRef = useRef();
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, contact: null });
  const [viewMode, setViewMode] = useState('chats'); // 'chats' | 'requests'
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' | 'direct' | 'group' | 'event'

  const chatDock = useChatDock();
  const pinnedContacts = chatDock?.pinnedContacts || [];
  const acceptRequest = chatDock?.acceptRequest;
  const rejectRequest = chatDock?.rejectRequest;
  const pendingRequestCount = requestContacts?.length || 0;
  const totalChatsCount = contacts?.length || 0;

  const isContactPinned = (contact) => {
    if (!contact) return false;
    const type = contact.type || 'direct';
    return pinnedContacts.some(p => p.contact_id === contact.id && p.contact_type === type);
  };

  const handleTogglePin = (contact) => {
    if (!chatDock || !contact) return;
    const type = contact.type || 'direct';
    if (isContactPinned(contact)) {
      chatDock.unpinContact?.(contact.id, type);
    } else {
      chatDock.pinContact?.(contact.id, type);
    }
  };

  useEffect(() => {
    if (!ctxMenu.open) return;
    const close = () => setCtxMenu({ open: false, x: 0, y: 0, contact: null });
    const onDocClick = (e) => {
      close();
    };
    const onEscape = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDocClick);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onEscape);
    };
  }, [ctxMenu.open]);

  // Keep focused item in sync with activeContact (only in Chats view)
  useEffect(() => {
    const source = viewMode === 'requests' ? requestContacts : contacts;
    const idx = source.findIndex(c => c.id === activeContact);
    setFocusedIndex(idx);
  }, [activeContact, contacts, requestContacts, viewMode]);

  const onKeyDown = (e) => {
    const source = viewMode === 'requests' ? requestContacts : contacts;
    if (!source || source.length === 0) return;
    const max = source.length - 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(max, (focusedIndex >= 0 ? focusedIndex + 1 : 0));
      setFocusedIndex(next);
      scrollIntoView(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = Math.max(0, (focusedIndex > 0 ? focusedIndex - 1 : 0));
      setFocusedIndex(prev);
      scrollIntoView(prev);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusedIndex(0);
      scrollIntoView(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setFocusedIndex(max);
      scrollIntoView(max);
    } else if (e.key === 'Enter') {
      if (focusedIndex >= 0) {
        e.preventDefault();
        setActiveContact(source[focusedIndex].id);
      }
    }
  };

  const scrollIntoView = (index) => {
    try {
      const el = listRef.current?.querySelector(`[data-index="${index}"]`);
      el && el.scrollIntoView({ block: 'nearest' });
    } catch {}
  };

  const applyCategoryFilter = (items) => {
    if (!items || items.length === 0) return [];
    // In Requests view we ignore category filters and just show the request list as-is
    if (viewMode === 'requests') return items;
    if (categoryFilter === 'direct') return items.filter(c => c.type === 'direct');
    if (categoryFilter === 'group') return items.filter(c => c.type === 'group' || c.type === 'academy_group');
    if (categoryFilter === 'event') return items.filter(c => c.type === 'event');
    return items;
  };

  const baseContacts = (viewMode === 'requests' ? requestContacts : contacts) || [];
  const categoryFiltered = applyCategoryFilter(baseContacts);
  const filteredDirect = categoryFiltered.filter(c => c.type === 'direct');
  const filteredGroups = categoryFiltered.filter(c => c.type === 'group' || c.type === 'academy_group');
  const filteredEvents = categoryFiltered.filter(c => c.type === 'event');

  const renderContactItem = (contact) => {
    const indexSource = viewMode === 'requests' ? requestContacts : contacts;
    const index = indexSource.findIndex(c => c.id === contact.id);
    const isActive = activeContact === contact.id;
    const isFocused = focusedIndex === index;
    const label = contact.name || contact.email || 'Group';
    const last = lastMessages[contact.id];
    let lastText = last ? (last.media_url ? '📎 Attachment' : (last.content || '')) : '';
    if (contact.type === 'group' && lastText) {
      const senderName = (lastSenderNames && lastSenderNames[contact.id]) || '';
      if (senderName) lastText = `${senderName}: ${lastText}`;
    }
    const time = last ? new Date(last.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
    const unread = unreadCounts[contact.id] || 0;
    const enrichedData = enrichedContacts[contact.id] || { id: contact.id, display_name: label, avatar_url: contact.avatar_url };

    return (
      <div
        key={`${contact.type}-${contact.id}`}
        className={`contact-item ${isActive ? 'active' : ''} ${isFocused ? 'focused' : ''}`}
        onClick={() => setActiveContact(contact.id)}
        onContextMenu={(e) => {
          e.preventDefault();
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            const padding = 8;
            const estimatedWidth = 220;
            const relClickX = e.clientX - containerRect.left;
            const maxX = Math.max(containerRect.width - estimatedWidth - padding, padding);
            const x = Math.min(Math.max(relClickX, padding), maxX);
            const y = e.clientY - containerRect.top;
            setCtxMenu({ open: true, x, y, contact });
          } else {
            setCtxMenu({ open: true, x: e.clientX, y: e.clientY, contact });
          }
        }}
        role="option"
        aria-selected={isActive}
        data-index={index}
      >
        {contact.type === 'group' && enrichedData.group_banner_url ? (
          <HoverCard type="group" context="messenger" data={enrichedData}>
            <img src={enrichedData.group_banner_url} alt={label} className="contact-avatar" style={{ objectFit: 'cover' }} />
          </HoverCard>
        ) : contact.type === 'direct' && contact.avatar_url ? (
          <HoverCard type="profile" context="messenger" data={enrichedData}>
            <img src={contact.avatar_url} alt={label} className="contact-avatar" />
          </HoverCard>
        ) : (
          <HoverCard type={contact.type === 'group' ? 'group' : 'profile'} context="messenger" data={enrichedData}>
            <div className={`contact-avatar ${contact.type}`}>
              {getInitials(label)}
              <span className={`status-indicator ${contact.type === 'group' ? 'group' : 'online'}`}></span>
            </div>
          </HoverCard>
        )}
        <div className="contact-info">
          <div className="contact-name-time">
            <HoverCard type={contact.type === 'group' ? 'group' : 'profile'} context="messenger" data={enrichedData}>
              <h3 title={label} style={{ cursor: 'pointer' }}>{label}</h3>
            </HoverCard>
            {time && <span className="message-time">{time}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {lastText && <div className="last-message" title={lastText}>{lastText}</div>}
            {unread > 0 && <span className="unread-badge" aria-label={`${unread} unread`}>{unread}</span>}
          </div>
        </div>
        {viewMode === 'requests' && (
          <div className="request-actions">
            <button
              type="button"
              className="request-btn accept"
              onClick={(e) => {
                e.stopPropagation();
                acceptRequest?.(contact.id);
              }}
            >
              Accept
            </button>
            <button
              type="button"
              className="request-btn reject"
              onClick={(e) => {
                e.stopPropagation();
                rejectRequest?.(contact.id);
              }}
            >
              Reject
            </button>
          </div>
        )}
        {/* Actions moved to ChatHeader thread actions menu; right-click menu also available */}
      </div>
    );
  };

  return (
    <div className="contacts-sidebar" ref={containerRef}>
      <div className="contacts-header">
        <div className="contacts-header-bar">
          <div className="contacts-view-toggle" role="tablist" aria-label="Chats or requests">
            <button
              type="button"
              className={`contacts-view-tab ${viewMode === 'chats' ? 'active' : ''}`}
              role="tab"
              aria-selected={viewMode === 'chats'}
              aria-label="Chats"
              title="Chats"
              onClick={() => setViewMode('chats')}
            >
              <FaComments aria-hidden="true" />
              {totalChatsCount > 0 && (
                <span className="contacts-tab-count">{totalChatsCount > 9 ? '9+' : totalChatsCount}</span>
              )}
            </button>
            <button
              type="button"
              className={`contacts-view-tab ${viewMode === 'requests' ? 'active' : ''}`}
              role="tab"
              aria-selected={viewMode === 'requests'}
              aria-label="Message requests"
              title="Message requests"
              onClick={() => setViewMode('requests')}
            >
              <FaEnvelopeOpen aria-hidden="true" />
              {pendingRequestCount > 0 && (
                <span className="contacts-tab-count">{pendingRequestCount > 9 ? '9+' : pendingRequestCount}</span>
              )}
            </button>
          </div>
          <button
            className="contacts-settings-btn"
            onClick={onOpenSettings}
            title="Messenger settings"
            aria-label="Messenger settings"
          >
            ⚙️
          </button>
        </div>
        {viewMode === 'chats' && (
          <div className="contacts-filter-icons" aria-label="Filter chats" role="group">
            <button
              type="button"
              className={`contacts-filter-icon ${categoryFilter === 'all' ? 'active' : ''}`}
              title="All"
              onClick={() => setCategoryFilter('all')}
            >
              ●
            </button>
            <button
              type="button"
              className={`contacts-filter-icon ${categoryFilter === 'direct' ? 'active' : ''}`}
              title="Connections"
              onClick={() => setCategoryFilter('direct')}
            >
              👤
            </button>
            <button
              type="button"
              className={`contacts-filter-icon ${categoryFilter === 'group' ? 'active' : ''}`}
              title="Groups"
              onClick={() => setCategoryFilter('group')}
            >
              👥
            </button>
            <button
              type="button"
              className={`contacts-filter-icon ${categoryFilter === 'event' ? 'active' : ''}`}
              title="Events"
              onClick={() => setCategoryFilter('event')}
            >
              📅
            </button>
          </div>
        )}
      </div>
      <div className="contacts-search">
        <input
          type="text"
          placeholder="Search"
          value={query}
          onChange={(e) => {
            const v = e.target.value;
            setQuery(v);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
              onSearchChange && onSearchChange(v);
            }, 250);
          }}
        />
      </div>
      <div
        className="contacts-list"
        role="listbox"
        aria-label={viewMode === 'chats' ? 'Chats' : 'Message requests'}
        tabIndex={0}
        onKeyDown={onKeyDown}
        ref={listRef}
      >
        {categoryFiltered.length === 0 && (
          <div style={{ padding: '10px', color: 'var(--text-muted)' }}>
            {query
              ? 'No results'
              : (viewMode === 'requests' ? 'No requests to display' : 'No chats to display')}
          </div>
        )}

        {categoryFiltered.length > 0 && (
          viewMode === 'requests'
            ? (
              <>
                <div className="contacts-section-label">Requests ({categoryFiltered.length})</div>
                {categoryFiltered.map(renderContactItem)}
              </>
            )
            : (
              categoryFilter === 'all' ? (
                <>
                  {filteredDirect.length > 0 && (
                    <>
                      <div className="contacts-section-label">Connections ({filteredDirect.length})</div>
                      {filteredDirect.map(renderContactItem)}
                    </>
                  )}
                  {filteredGroups.length > 0 && (
                    <>
                      <div className="contacts-section-label">Groups ({filteredGroups.length})</div>
                      {filteredGroups.map(renderContactItem)}
                    </>
                  )}
                  {filteredEvents.length > 0 && (
                    <>
                      <div className="contacts-section-label">Events ({filteredEvents.length})</div>
                      {filteredEvents.map(renderContactItem)}
                    </>
                  )}
                </>
              ) : categoryFilter === 'direct' ? (
                <>
                  <div className="contacts-section-label">Connections ({filteredDirect.length})</div>
                  {filteredDirect.map(renderContactItem)}
                </>
              ) : categoryFilter === 'group' ? (
                <>
                  <div className="contacts-section-label">Groups ({filteredGroups.length})</div>
                  {filteredGroups.map(renderContactItem)}
                </>
              ) : (
                <>
                  <div className="contacts-section-label">Events ({filteredEvents.length})</div>
                  {filteredEvents.map(renderContactItem)}
                </>
              )
            )
        )}
      </div>
      <div className="contacts-footer">
        <button className="new-group-btn" onClick={onCreateGroup}>+ New Group</button>
      </div>
      {ctxMenu.open && ctxMenu.contact && (
        <div className="contacts-context-menu" style={{ top: ctxMenu.y, left: ctxMenu.x }}>
          <button className="ctx-item" onClick={() => { onMarkUnread?.(ctxMenu.contact); setCtxMenu({ open: false, x: 0, y: 0, contact: null }); }}>Mark as unread</button>
          <button
            className="ctx-item"
            onClick={() => {
              handleTogglePin(ctxMenu.contact);
              setCtxMenu({ open: false, x: 0, y: 0, contact: null });
            }}
          >
            {isContactPinned(ctxMenu.contact) ? 'Unpin conversation' : 'Pin conversation'}
          </button>
          {ctxMenu.contact.type === 'group' && (
            <>
              <button className="ctx-item" onClick={() => { onEditGroup?.(ctxMenu.contact); setCtxMenu({ open: false, x: 0, y: 0, contact: null }); }}>Edit group</button>
              <button className="ctx-item danger" onClick={() => { onRemoveGroup?.(ctxMenu.contact.id); setCtxMenu({ open: false, x: 0, y: 0, contact: null }); }}>Remove group</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactsSidebar;